import React, { Component } from 'react';
import { X, Package, Calendar, MapPin, Phone, Mail as MailIcon, CheckCircle } from 'lucide-react';
import { Booking } from '../types/booking';
import { motion, AnimatePresence } from 'framer-motion';
interface HandoverModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: Booking;
}
export function HandoverModal({
  isOpen,
  onClose,
  booking
}: HandoverModalProps) {
  const handleContactNextFarmer = () => {
    const subject = `Andros Ultra Binder Handover - ${booking.handoverDate}`;
    const body = `Hi ${booking.nextFarmerName},

I'm finishing my booking with the Andros Ultra Binder on ${booking.endDate}.

I understand you're picking it up on ${booking.handoverDate}.

Current location: ${booking.location}
My contact: ${booking.phone || '[Your phone]'}

Please let me know a convenient pickup time.

Best regards,
${booking.userName}`;
    const recipient = booking.nextFarmerEmail;
    const mailtoLink = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };
  const handleConfirmHandover = () => {
    const subject = `Handover Confirmed - Booking #${booking.id}`;
    const body = `Hi Team,

I confirm that I have handed over the Andros Ultra Binder to ${booking.nextFarmerName}.

Handover Details:
- From: ${booking.userName} (${booking.farm || booking.location})
- To: ${booking.nextFarmerName} (${booking.nextFarmLocation})
- Date: ${new Date().toLocaleDateString('en-AU')}
- Equipment Condition: [Please describe Good / Fair / Needs Attention]
- Additional Notes: [Any issues or observations]

Best regards,
${booking.userName}`;
    const recipient = 'agplastic.SDRC@gmail.com';
    const mailtoLink = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };
  return <>
      <AnimatePresence data-id="element-259">
        {isOpen && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={onClose} className="fixed inset-0 bg-black/50 z-50" data-id="element-260" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-261">
              <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-id="element-262">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between" data-id="element-263">
                  <div className="flex items-center gap-3" data-id="element-264">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-265">
                      <Package className="text-blue-600" size={20} data-id="element-266" />
                    </div>
                    <div data-id="element-267">
                      <h2 className="text-lg font-bold text-slate-900" data-id="element-268">
                        Handover Instructions
                      </h2>
                      <p className="text-xs text-slate-500" data-id="element-269">
                        Coordinate with next farmer
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-270">
                    <X size={20} data-id="element-271" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6" data-id="element-272">
                  {/* Next Farmer Details */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-273">
                    <h3 className="font-semibold text-slate-900 mb-3" data-id="element-274">
                      Next Farmer
                    </h3>
                    <div className="space-y-2" data-id="element-275">
                      <div className="flex items-start gap-2" data-id="element-276">
                        <Package size={16} className="text-blue-600 mt-0.5" data-id="element-277" />
                        <div data-id="element-278">
                          <div className="font-medium text-slate-900" data-id="element-279">
                            {booking.nextFarmerName}
                          </div>
                          <div className="text-sm text-slate-600" data-id="element-280">
                            {booking.nextFarmLocation}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-281">
                        <Calendar size={16} className="text-blue-600" data-id="element-282" />
                        <span data-id="element-283">Pickup: {booking.handoverDate}</span>
                      </div>
                      {booking.nextFarmerPhone && <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-284">
                          <Phone size={16} className="text-blue-600" data-id="element-285" />
                          <span data-id="element-286">{booking.nextFarmerPhone}</span>
                        </div>}
                      {booking.nextFarmerEmail && <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-287">
                          <MailIcon size={16} className="text-blue-600" data-id="element-288" />
                          <span className="truncate" data-id="element-289">
                            {booking.nextFarmerEmail}
                          </span>
                        </div>}
                    </div>
                  </div>

                  {/* Current Location */}
                  <div data-id="element-290">
                    <h3 className="font-semibold text-slate-900 mb-2" data-id="element-291">
                      Current Location
                    </h3>
                    <div className="flex items-start gap-2 text-slate-600" data-id="element-292">
                      <MapPin size={16} className="text-slate-400 mt-0.5" data-id="element-293" />
                      <span data-id="element-294">{booking.location}</span>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div data-id="element-295">
                    <h3 className="font-semibold text-slate-900 mb-2" data-id="element-296">
                      Handover Checklist
                    </h3>
                    <ul className="space-y-2 text-sm text-slate-600" data-id="element-297">
                      <li className="flex items-start gap-2" data-id="element-298">
                        <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" data-id="element-299" />
                        <span data-id="element-300">
                          Coordinate pickup time with{' '}
                          {booking.nextFarmerName?.split(' ')[0]}
                        </span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-301">
                        <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" data-id="element-302" />
                        <span data-id="element-303">Ensure equipment is clean</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-304">
                        <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" data-id="element-305" />
                        <span data-id="element-306">Hand over user manual</span>
                      </li>
                      <li className="flex items-start gap-2" data-id="element-307">
                        <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" data-id="element-308" />
                        <span data-id="element-309">Report any issues or maintenance required</span>
                      </li>
                    </ul>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 pt-4 border-t border-slate-200" data-id="element-310">
                    <button onClick={handleContactNextFarmer} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors" data-id="element-311">
                      <MailIcon size={18} data-id="element-312" />
                      <span data-id="element-313">
                        Contact {booking.nextFarmerName?.split(' ')[0]} via
                        Email
                      </span>
                    </button>

                    <button onClick={handleConfirmHandover} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white text-slate-700 border border-slate-300 rounded-lg font-medium hover:bg-slate-50 transition-colors" data-id="element-314">
                      <CheckCircle size={18} data-id="element-315" />
                      <span data-id="element-316">Notify of Handover</span>
                    </button>
                  </div>

                  {/* Note */}
                  <p className="text-xs text-slate-500 text-center" data-id="element-317">
                    Notification is optional but helps track equipment location
                  </p>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}